import { zodResolver } from '@hookform/resolvers/zod';
import type { AdminSelf } from '@saay/api-client';
import { isApiError } from '@saay/api-client';
import { formatLocalDate, formatRatePercent, formatToman, toPersianDigits } from '@saay/formatting';
import { useQueryClient } from '@tanstack/react-query';
import { type CommandOutcome, useCommand } from '@saay/web-app';
import { type ReactNode, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { z } from 'zod';

import type {
  PolicyVersion,
  PolicyVersionCreate,
  WalletCreditPolicyVersion,
  WalletPolicyVersion,
  WalletTopUpFeePolicyVersion,
} from '../api.js';
import { AdminSolarDateField } from '../components/admin-date-field.js';
import { normalizeDecimalInput, useSubmitBlocked } from '../components/form.js';
import { QueryState } from '../components/guard.js';
import { LoadMore } from '../kit.js';
import {
  AccordionPanel,
  applyFieldErrors,
  Button,
  CommandFeedback,
  DetailGrid,
  EmptyState,
  MoneyField,
  Section,
  SideSheet,
  Skeleton,
  StatusChip,
  Switch,
  TextArea,
  TextField,
  useToast,
} from '../kit.js';
import { canManageWalletPolicy, type WalletPolicyType } from '../permissions.js';
import { listStatus, setupKeys, useCurrentPolicy, usePolicyVersions } from '../queries.js';
import { tehranToday } from '../validation.js';

type WalletPolicy = WalletPolicyVersion | WalletTopUpFeePolicyVersion | WalletCreditPolicyVersion;

function isWalletPolicy(version: PolicyVersion): version is WalletPolicy {
  return (
    version.policyType === 'wallet' ||
    version.policyType === 'wallet-top-up-fee' ||
    version.policyType === 'wallet-credit'
  );
}

/** Readable values of a wallet policy version (AC §30.6). Amounts stay the server's rials. */
export function walletPolicyRows(version: PolicyVersion): [string, ReactNode][] {
  if (!isWalletPolicy(version)) return [];
  if (version.policyType === 'wallet-credit') {
    return [
      ['ضریب اعتبار', toPersianDigits(version.multiplier)],
      ['اعتبار از کیف پول', version.enabled ? 'فعال' : 'خاموش'],
      ['روش محاسبه', 'ضریب ثابت'],
    ];
  }
  if (version.policyType === 'wallet-top-up-fee') {
    return [
      ...version.brackets.map((bracket, index): [string, ReactNode] => {
        const previous = version.brackets[index - 1]?.upToIrr ?? null;
        const label =
          bracket.upToIrr !== null
            ? `کارمزد شارژ تا ${formatToman(bracket.upToIrr)}`
            : previous === null
              ? 'کارمزد شارژ'
              : `کارمزد شارژ بیشتر از ${formatToman(previous)}`;
        return [label, formatRatePercent(bracket.rate, { invalid: 'raw' })];
      }),
      ['حداقل کارمزد هر شارژ', formatToman(version.minimumFeeIrr)],
    ];
  }
  return [
    ['حداقل شارژ', formatToman(version.topUpMinIrr)],
    ['حداکثر شارژ', formatToman(version.topUpMaxIrr)],
    ['سقف موجودی', formatToman(version.balanceCapIrr)],
    ['حداقل برداشت', formatToman(version.withdrawalMinIrr)],
    ['حداکثر برداشت', formatToman(version.withdrawalMaxIrr)],
    ['سقف برداشت روزانه', formatToman(version.withdrawalDailyMaxIrr)],
    ['مهلت پس از تغییر شماره یا شبا', `${toPersianDigits(version.withdrawalCooldownHours)} ساعت`],
    ['نگه‌داشتن شارژ قبل از خروج', `${toPersianDigits(version.topUpWithdrawalHoldHours)} ساعت`],
    ['شبا به نام خود خریدار', version.requireOwnNameSheba ? 'بله' : 'خیر'],
    ['حداقل انتقال', formatToman(version.transferMinIrr)],
    ['حداکثر انتقال', formatToman(version.transferMaxIrr)],
    ['سقف انتقال روزانه', formatToman(version.transferDailyMaxIrr)],
    ['سقف دریافت ماهانه', formatToman(version.transferMonthlyReceivedMaxIrr)],
    ['نگه‌داشتن انتقال دریافتی', `${toPersianDigits(version.transferWithdrawalHoldHours)} ساعت`],
    ['گیرنده باید احراز هویت شده باشد', version.transferRequireVerifiedRecipient ? 'بله' : 'خیر'],
  ];
}

/**
 * The reason shown for a policy version. The versions that migrations seeded carry only decision
 * ids («D-102, D-108»), which mean nothing on screen; they read as the initial setup.
 */
export function policyReasonText(reason: string): string {
  return /^\s*D-\d+(\s*,\s*D-\d+)*\s*$/.test(reason) ? 'تنظیم اولیهٔ سامانه' : reason;
}

const money = z.number().int().positive();

const walletSchema = z.object({
  balanceCapIrr: money,
  effectiveFrom: z.string().min(1, 'تاریخ شروع را وارد کنید.'),
  reason: z.string().trim().min(1, 'دلیل را بنویسید.').max(500, 'دلیل بلندتر از حد مجاز است.'),
  requireOwnNameSheba: z.boolean(),
  topUpMaxIrr: money,
  topUpMinIrr: money,
  topUpWithdrawalHoldHours: z.number().int().min(0).max(720),
  transferDailyMaxIrr: money,
  transferMaxIrr: money,
  transferMinIrr: money,
  transferMonthlyReceivedMaxIrr: money,
  transferRequireVerifiedRecipient: z.boolean(),
  transferWithdrawalHoldHours: z.number().int().min(0).max(720),
  withdrawalCooldownHours: z.number().int().min(0).max(720),
  withdrawalDailyMaxIrr: money,
  withdrawalMaxIrr: money,
  withdrawalMinIrr: money,
});

type WalletForm = z.infer<typeof walletSchema>;

const feeSchema = z.object({
  effectiveFrom: z.string().min(1, 'تاریخ شروع را وارد کنید.'),
  minimumFeeIrr: z.number().int().min(0),
  rate: z
    .string()
    .regex(/^\d\.\d{6}$/, 'نرخ را مثل ۰٫۰۱ وارد کنید (یک درصد؛ حداکثر شش رقم اعشار).'),
  reason: z.string().trim().min(1, 'دلیل را بنویسید.').max(500, 'دلیل بلندتر از حد مجاز است.'),
});

type FeeForm = z.infer<typeof feeSchema>;

const creditSchema = z.object({
  effectiveFrom: z.string().min(1, 'تاریخ شروع را وارد کنید.'),
  enabled: z.boolean(),
  multiplier: z
    .string()
    .regex(/^\d{1,3}\.\d{2}$/, 'ضریب را مثل ۵ یا ۵٫۵ وارد کنید (حداکثر دو رقم اعشار).')
    .refine((value) => {
      const numeric = Number(value);
      return numeric >= 1 && numeric <= 100;
    }, 'ضریب باید بین ۱ و ۱۰۰ باشد.'),
  reason: z.string().trim().min(1, 'دلیل را بنویسید.').max(500, 'دلیل بلندتر از حد مجاز است.'),
});

type CreditForm = z.infer<typeof creditSchema>;

const WALLET_MONEY = [
  ['topUpMinIrr', 'حداقل شارژ'],
  ['topUpMaxIrr', 'حداکثر شارژ'],
  ['balanceCapIrr', 'سقف موجودی'],
  ['withdrawalMinIrr', 'حداقل برداشت'],
  ['withdrawalMaxIrr', 'حداکثر برداشت'],
  ['withdrawalDailyMaxIrr', 'سقف برداشت روزانه'],
  ['transferMinIrr', 'حداقل انتقال'],
  ['transferMaxIrr', 'حداکثر انتقال'],
  ['transferDailyMaxIrr', 'سقف انتقال روزانه'],
  ['transferMonthlyReceivedMaxIrr', 'سقف دریافت ماهانه'],
] as const;

const WALLET_HOURS = [
  ['withdrawalCooldownHours', 'مهلت پس از تغییر شماره یا شبا (ساعت)'],
  ['topUpWithdrawalHoldHours', 'نگه‌داشتن شارژ قبل از خروج (ساعت)'],
  ['transferWithdrawalHoldHours', 'نگه‌داشتن انتقال دریافتی (ساعت)'],
] as const;

function walletDefaults(latest: WalletPolicyVersion | undefined, earliest: string): WalletForm {
  return {
    balanceCapIrr: latest?.balanceCapIrr ?? 2_000_000_000,
    effectiveFrom: earliest,
    reason: '',
    requireOwnNameSheba: latest?.requireOwnNameSheba ?? false,
    topUpMaxIrr: latest?.topUpMaxIrr ?? 500_000_000,
    topUpMinIrr: latest?.topUpMinIrr ?? 1_000_000,
    topUpWithdrawalHoldHours: latest?.topUpWithdrawalHoldHours ?? 72,
    transferDailyMaxIrr: latest?.transferDailyMaxIrr ?? 200_000_000,
    transferMaxIrr: latest?.transferMaxIrr ?? 100_000_000,
    transferMinIrr: latest?.transferMinIrr ?? 100_000,
    transferMonthlyReceivedMaxIrr: latest?.transferMonthlyReceivedMaxIrr ?? 2_000_000_000,
    transferRequireVerifiedRecipient: latest?.transferRequireVerifiedRecipient ?? false,
    transferWithdrawalHoldHours: latest?.transferWithdrawalHoldHours ?? 0,
    withdrawalCooldownHours: latest?.withdrawalCooldownHours ?? 72,
    withdrawalDailyMaxIrr: latest?.withdrawalDailyMaxIrr ?? 1_000_000_000,
    withdrawalMaxIrr: latest?.withdrawalMaxIrr ?? 500_000_000,
    withdrawalMinIrr: latest?.withdrawalMinIrr ?? 1_000_000,
  };
}

function PolicyEditor({
  expectedVersion,
  latest,
  onDone,
  type,
}: {
  readonly expectedVersion: number;
  readonly latest: PolicyVersion | undefined;
  readonly onDone: () => void;
  readonly type: WalletPolicyType;
}) {
  const toast = useToast();
  const queryClient = useQueryClient();
  const offline = useSubmitBlocked();
  const today = tehranToday();
  const walletLatest = latest?.policyType === 'wallet' ? latest : undefined;
  const feeLatest = latest?.policyType === 'wallet-top-up-fee' ? latest : undefined;
  const creditLatest = latest?.policyType === 'wallet-credit' ? latest : undefined;
  const earliest =
    latest !== undefined && latest.effectiveFrom > today ? latest.effectiveFrom : today;
  const [conflict, setConflict] = useState(false);
  const walletForm = useForm<WalletForm>({
    defaultValues: walletDefaults(walletLatest, earliest),
    resolver: zodResolver(walletSchema),
  });
  const feeForm = useForm<FeeForm>({
    defaultValues: {
      effectiveFrom: earliest,
      minimumFeeIrr: feeLatest?.minimumFeeIrr ?? 17_000,
      rate: feeLatest?.brackets[0]?.rate ?? '0.010000',
      reason: '',
    },
    resolver: zodResolver(feeSchema),
  });
  const creditForm = useForm<CreditForm>({
    defaultValues: {
      effectiveFrom: earliest,
      enabled: creditLatest?.enabled ?? true,
      multiplier: creditLatest?.multiplier ?? '5.00',
      reason: '',
    },
    resolver: zodResolver(creditSchema),
  });
  const command = useCommand<PolicyVersionCreate, PolicyVersion>(
    `policy.${type}.create`,
    (client, body, intent) => client.post(`/admin/policies/${type}/versions`, { body, intent }),
    {
      onApprovalPending: (_approval, result) => {
        toast.success({ confirmedBy: result, title: 'برای تأیید ارسال شد' });
        onDone();
      },
      onSuccess: async (result) => {
        await queryClient.invalidateQueries({ queryKey: setupKeys.policy(type) });
        toast.success({
          confirmedBy: result,
          description:
            result.data.status === 'SCHEDULED'
              ? `از ${formatLocalDate(result.data.effectiveFrom, 'long')} اعمال می‌شود.`
              : 'از همین لحظه اعمال شد.',
          title: `نسخه ${toPersianDigits(result.data.version)} ثبت شد`,
        });
        onDone();
      },
    },
  );

  function handle(outcome: CommandOutcome<PolicyVersion>) {
    if (outcome.status !== 'error' || !isApiError(outcome.error)) return;
    const error = outcome.error;
    if (type === 'wallet') {
      applyFieldErrors(
        error,
        [
          'effectiveFrom',
          'reason',
          ...WALLET_MONEY.map(([name]) => name),
          ...WALLET_HOURS.map(([name]) => name),
        ],
        walletForm.setError,
      );
    } else if (type === 'wallet-credit') {
      applyFieldErrors(
        error,
        ['effectiveFrom', 'multiplier', 'enabled', 'reason'],
        creditForm.setError,
      );
    } else {
      applyFieldErrors(
        error,
        ['effectiveFrom', 'minimumFeeIrr', 'rate', 'reason'],
        feeForm.setError,
      );
    }
    if (error.code === 'RESOURCE_VERSION_CONFLICT') {
      setConflict(true);
      void queryClient.invalidateQueries({ queryKey: setupKeys.policy(type) });
    }
    if (error.code === 'WALLET_SHEBA_INQUIRY_NOT_CONFIGURED') {
      walletForm.setError('requireOwnNameSheba', { message: error.messageFa, type: 'server' });
    }
  }

  async function submitWallet(values: WalletForm) {
    setConflict(false);
    handle(
      await command.submit({
        ...values,
        expectedVersion,
        policyType: 'wallet',
      }),
    );
  }

  async function submitFee(values: FeeForm) {
    setConflict(false);
    handle(
      await command.submit({
        brackets: [{ rate: values.rate, upToIrr: null }],
        effectiveFrom: values.effectiveFrom,
        expectedVersion,
        minimumFeeIrr: values.minimumFeeIrr,
        policyType: 'wallet-top-up-fee',
        reason: values.reason,
      }),
    );
  }

  async function submitCredit(values: CreditForm) {
    setConflict(false);
    handle(
      await command.submit({
        effectiveFrom: values.effectiveFrom,
        enabled: values.enabled,
        expectedVersion,
        multiplier: values.multiplier,
        policyType: 'wallet-credit',
        reason: values.reason,
        strategyCode: 'FIXED_MULTIPLIER',
      }),
    );
  }

  // Reading `formState.errors` subscribes this render to client-side messages. `getFieldState`
  // alone does not, so a failed submit left the reason and the number fields unmarked.
  const walletErrors = walletForm.formState.errors;
  const feeErrors = feeForm.formState.errors;
  const creditErrors = creditForm.formState.errors;
  const feedback =
    command.state.status === 'error' &&
    isApiError(command.state.error) &&
    (command.state.error.code === 'RESOURCE_VERSION_CONFLICT' ||
      command.state.error.code === 'WALLET_SHEBA_INQUIRY_NOT_CONFIGURED' ||
      command.state.error.code === 'REQUEST_INVALID')
      ? { ...command.state, status: 'idle' as const }
      : command.state;

  return (
    <form
      aria-label="نسخه جدید سیاست"
      className="admin-form"
      noValidate
      onSubmit={
        type === 'wallet'
          ? walletForm.handleSubmit(submitWallet)
          : type === 'wallet-top-up-fee'
            ? feeForm.handleSubmit(submitFee)
            : creditForm.handleSubmit(submitCredit)
      }
    >
      <p className="admin-muted">
        {`نسخه جدید روی آخرین نسخه (${toPersianDigits(expectedVersion)}) ساخته می‌شود.`}
        {type === 'wallet-credit'
          ? ' تا وقتی تأیید دومرحله‌ای این سیاست خاموش است، ثبت بلافاصله اعمال می‌شود.'
          : ''}
      </p>
      {conflict ? (
        <p role="alert">این اطلاعات در این فاصله تغییر کرده است؛ دوباره بارگذاری کنید.</p>
      ) : null}
      {type === 'wallet' ? (
        <Controller
          control={walletForm.control}
          name="effectiveFrom"
          render={({ field, fieldState }) => (
            <AdminSolarDateField
              error={fieldState.error?.message}
              label="تاریخ شروع اعتبار"
              name={field.name}
              onBlur={field.onBlur}
              onChange={field.onChange}
              value={field.value}
            />
          )}
        />
      ) : null}
      {type === 'wallet-top-up-fee' ? (
        <Controller
          control={feeForm.control}
          name="effectiveFrom"
          render={({ field, fieldState }) => (
            <AdminSolarDateField
              error={fieldState.error?.message}
              label="تاریخ شروع اعتبار"
              name={field.name}
              onBlur={field.onBlur}
              onChange={field.onChange}
              value={field.value}
            />
          )}
        />
      ) : null}
      {type === 'wallet-credit' ? (
        <Controller
          control={creditForm.control}
          name="effectiveFrom"
          render={({ field, fieldState }) => (
            <AdminSolarDateField
              error={fieldState.error?.message}
              label="تاریخ شروع اعتبار"
              name={field.name}
              onBlur={field.onBlur}
              onChange={field.onChange}
              value={field.value}
            />
          )}
        />
      ) : null}
      {type === 'wallet'
        ? WALLET_MONEY.map(([name, label]) => (
            <Controller
              control={walletForm.control}
              key={name}
              name={name}
              render={({ field, fieldState }) => (
                <MoneyField
                  error={fieldState.error?.message}
                  label={label}
                  onChangeRials={field.onChange}
                  valueRials={typeof field.value === 'number' ? field.value : null}
                />
              )}
            />
          ))
        : null}
      {type === 'wallet'
        ? WALLET_HOURS.map(([name, label]) => (
            <TextField
              error={walletErrors[name]?.message}
              inputMode="numeric"
              key={name}
              label={label}
              ltr
              type="number"
              {...walletForm.register(name, { valueAsNumber: true })}
            />
          ))
        : null}
      {type === 'wallet' ? (
        <>
          <Controller
            control={walletForm.control}
            name="requireOwnNameSheba"
            render={({ field, fieldState }) => (
              <>
                <Switch
                  checked={field.value}
                  label="شبا باید به نام خود خریدار باشد"
                  onCheckedChange={field.onChange}
                />
                {fieldState.error?.message === undefined ? null : (
                  <p role="alert">{fieldState.error.message}</p>
                )}
              </>
            )}
          />
          <Controller
            control={walletForm.control}
            name="transferRequireVerifiedRecipient"
            render={({ field }) => (
              <Switch
                checked={field.value}
                label="گیرنده باید احراز هویت شده باشد"
                onCheckedChange={field.onChange}
              />
            )}
          />
        </>
      ) : null}
      {type === 'wallet-top-up-fee' ? (
        <>
          <TextField
            error={feeErrors.rate?.message}
            hint="مثلاً ۰٫۰۱ یعنی یک درصد."
            label="نرخ کارمزد"
            ltr
            {...feeForm.register('rate', {
              setValueAs: (value: unknown) => normalizeDecimalInput(value, 6),
            })}
          />
          <Controller
            control={feeForm.control}
            name="minimumFeeIrr"
            render={({ field, fieldState }) => (
              <MoneyField
                error={fieldState.error?.message}
                label="حداقل کارمزد"
                onChangeRials={field.onChange}
                valueRials={field.value}
              />
            )}
          />
        </>
      ) : null}
      {type === 'wallet-credit' ? (
        <>
          <TextField
            error={creditErrors.multiplier?.message}
            hint="از ۱ تا ۱۰۰؛ مثل ۵ یا ۵٫۵"
            label="ضریب اعتبار"
            ltr
            {...creditForm.register('multiplier', {
              setValueAs: (value: unknown) => normalizeDecimalInput(value, 2),
            })}
          />
          <Controller
            control={creditForm.control}
            name="enabled"
            render={({ field }) => (
              <Switch
                checked={field.value}
                label="اعتبار از کیف پول فعال باشد"
                onCheckedChange={field.onChange}
              />
            )}
          />
        </>
      ) : null}
      {type === 'wallet' ? (
        <TextArea
          error={walletErrors.reason?.message}
          label="دلیل تغییر"
          {...walletForm.register('reason')}
        />
      ) : null}
      {type === 'wallet-top-up-fee' ? (
        <TextArea
          error={feeErrors.reason?.message}
          label="دلیل تغییر"
          {...feeForm.register('reason')}
        />
      ) : null}
      {type === 'wallet-credit' ? (
        <TextArea
          error={creditErrors.reason?.message}
          label="دلیل تغییر"
          {...creditForm.register('reason')}
        />
      ) : null}
      <CommandFeedback
        onCheckStatus={() => void command.checkStatus().then(handle)}
        state={feedback}
      />
      <div className="admin-actions admin-actions--end">
        <Button
          disabled={offline.blocked}
          loading={command.state.status === 'pending'}
          type="submit"
        >
          ثبت نسخه جدید
        </Button>
      </div>
    </form>
  );
}

export function WalletPolicyPanel({
  self,
  type,
}: {
  readonly self: AdminSelf;
  readonly type: WalletPolicyType;
}) {
  const current = useCurrentPolicy(type);
  const versions = usePolicyVersions(type);
  const manage = canManageWalletPolicy(self);
  const rows = versions.data?.pages.flatMap((page) => page.items) ?? [];
  const latest = [...rows].sort((left, right) => right.version - left.version)[0];
  const noCurrent = isApiError(current.error) && current.error.status === 404;
  const [editOpen, setEditOpen] = useState(false);
  const shown = noCurrent ? latest : current.data;

  return (
    <div className="admin-sections">
      <Section id={`policy-${type}-current`} title="نسخه جاری">
        {noCurrent && versions.status === 'success' && rows.length === 0 ? (
          <EmptyState
            {...(manage
              ? {
                  action: (
                    <Button onClick={() => setEditOpen(true)} variant="primary">
                      تعریف اولین نسخه
                    </Button>
                  ),
                }
              : {})}
            description="هنوز نسخه‌ای از این سیاست ثبت نشده است."
            title="تعریف نشده"
          />
        ) : noCurrent && shown !== undefined && isWalletPolicy(shown) ? (
          <>
            <p>فعلاً هیچ نسخه‌ای جاری نیست.</p>
            <div className="admin-actions admin-actions--end">
              {manage ? (
                <Button onClick={() => setEditOpen(true)} size="sm" variant="secondary">
                  نسخه جدید
                </Button>
              ) : null}
            </div>
            <p>
              {`نسخه ${toPersianDigits(shown.version)} — از ${formatLocalDate(shown.effectiveFrom, 'long')} `}
              <StatusChip enumName="policyVersionStatus" value={shown.status} />
            </p>
            <DetailGrid
              items={walletPolicyRows(shown).map(([label, value]) => ({
                key: label,
                label,
                value,
              }))}
            />
          </>
        ) : (
          <QueryState
            error={current.error}
            onRetry={() => void current.refetch()}
            skeleton={<Skeleton label="در حال بارگذاری نسخه جاری…" />}
            status={current.status}
          >
            {current.data === undefined || !isWalletPolicy(current.data) ? null : (
              <>
                <div className="admin-actions admin-actions--end">
                  {manage ? (
                    <Button onClick={() => setEditOpen(true)} size="sm" variant="secondary">
                      ویرایش
                    </Button>
                  ) : null}
                </div>
                <p>
                  {`نسخه ${toPersianDigits(current.data.version)} — از ${formatLocalDate(current.data.effectiveFrom, 'long')} `}
                  <StatusChip enumName="policyVersionStatus" value={current.data.status} />
                </p>
                <DetailGrid
                  items={walletPolicyRows(current.data).map(([label, value]) => ({
                    key: label,
                    label,
                    value,
                  }))}
                />
              </>
            )}
          </QueryState>
        )}
      </Section>
      {manage ? (
        <SideSheet
          onOpenChange={setEditOpen}
          open={editOpen}
          title={rows.length === 0 ? 'اولین نسخه' : 'نسخه جدید'}
        >
          {versions.status === 'success' ? (
            <PolicyEditor
              expectedVersion={latest?.version ?? 0}
              key={`${type}:${latest?.version ?? 0}`}
              latest={latest}
              onDone={() => setEditOpen(false)}
              type={type}
            />
          ) : (
            <Skeleton label="در حال آماده‌سازی فرم…" />
          )}
        </SideSheet>
      ) : null}
      <Section id={`policy-${type}-history`} title="سابقه نسخه‌ها">
        <QueryState
          empty={<EmptyState title="نسخه‌ای ثبت نشده است" />}
          error={versions.error}
          isEmpty={rows.length === 0}
          onRetry={() => void versions.refetch()}
          skeleton={<Skeleton label="در حال بارگذاری سابقه…" lines={4} />}
          status={listStatus(versions)}
        >
          <AccordionPanel
            items={[...rows]
              .sort((left, right) => right.version - left.version)
              .map((row) => ({
                content: (
                  <DetailGrid
                    items={walletPolicyRows(row).map(([label, value]) => ({
                      key: label,
                      label,
                      value,
                    }))}
                  />
                ),
                key: row.id,
                summary: policyReasonText(row.reason),
                title: `نسخه ${toPersianDigits(row.version)} · ${formatLocalDate(row.effectiveFrom)}`,
              }))}
            label="سابقه نسخه‌ها"
          />
          <LoadMore
            hasMore={versions.hasNextPage}
            loading={versions.isFetchingNextPage}
            onLoadMore={() => void versions.fetchNextPage()}
          />
        </QueryState>
      </Section>
    </div>
  );
}
